import { ArchivedShiftIcon, FaIcon, HotShiftIcon, RotationIcon, SeriesIcon, ShiftsIcon } from '@atoms/icons';
import { Flex, PrimaryPaper, RoundPaper } from '@atoms/layout';
import { Shift } from '@libs/models/shifts';
import { getShiftType } from '@libs/models/shifts/shift';
import {
  alpha,
  Avatar,
  Box,
  Button,
  Chip,
  LinearProgress,
  Menu,
  MenuItem,
  Stack,
  StackOwnProps,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { ChangeEventHandler, useEffect, useState, useMemo, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Avatar as AvatarInt, TooltipNote } from '@atoms/dataDisplay';
import { useAuthStore } from '@libs/store/auth';
import {
  CANCEL_ALLOWED_STATUSES,
  DELETE_ALLOWED_STATUSES,
  EDIT_ALLOWED_STATUSES,
  UNASSIGN_ALLOWED_STATUSES,
} from '@pages/schedule/constants';
import { useShiftAddEditStore } from '@libs/store/shifts';
import { useWidthResize, useHeightResize } from './useResize';
import * as React from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { styled } from '@mui/system';
import { useCalendarShiftsStore } from '@libs/store/shifts/calendar';
import { useOrganizationDetailsStore } from '@libs/store/settings';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { EmptyListPlaceholder } from '@molecules/dataDisplay';
import { useLocationStore } from '@libs/store/locations';
import { StaffListItem } from '@libs/models/talents/talent';

type Row = {
  resourceInfo: StaffListItem;
  assignments: Shift[];
};

type Group = {
  groupId: string;
  groupName: string;
  items: Row[];
};

type CalendarProps = {
  startDate: Dayjs;
  endDate: Dayjs;
  groups: Group[];
  providers: StaffListItem[];
  unassigned: Shift[];
  dataLoading: boolean;
  onAssign: (provider: StaffListItem, shift: Shift) => Promise<void>;
  onAssignMenuOpen: (provider: StaffListItem, assignments: Shift[], day: Dayjs) => void;
  onShiftEdit: (shift: Shift) => void;
  onShiftCancelCall: (shift: Shift) => void;
  onShiftDeleteCall: (shift: Shift) => void;
  onShiftHardDeleteCall: (shift: Shift) => void;
  onShiftUnassignCall: (shift: Shift) => void;
  onShiftOpenSeries: (shift: Shift) => void;
  onShiftOpenRotation: (shift: Shift) => void;
  onResourceClick?: (provider: StaffListItem) => void;
  onSortingChange?: (field: string, type: 'asc' | 'desc' | null) => void;
};

const LS_CalendarSettings = 'userdata:calendarSettings';
const initialSettings = JSON.parse(localStorage.getItem(LS_CalendarSettings) || '{}');

export function Calendar({
  startDate,
  endDate,
  providers,
  groups,
  unassigned,
  dataLoading,
  onAssign,
  onAssignMenuOpen,
  onShiftEdit,
  onShiftCancelCall,
  onShiftDeleteCall,
  onShiftHardDeleteCall,
  onShiftUnassignCall,
  onShiftOpenSeries,
  onShiftOpenRotation,
  onResourceClick,
  onSortingChange,
}: CalendarProps) {
  const theme = useTheme();
  const { openShiftDrawer } = useShiftAddEditStore();
  const { data } = useOrganizationDetailsStore();
  const { setSelectedShift } = useCalendarShiftsStore();
  const { width, startResize: startWidthResize } = useWidthResize({
    minWidth: 200,
    maxWidth: 600,
    initialWidth: initialSettings.calendarWidth || 300,
  });

  const { height, startResize: startHeightResize } = useHeightResize({
    minHeight: 200,
    maxHeight: 600,
    initialHeight: initialSettings.calendarHeight || 300,
  });
  const [isUnassignedDrawerOpen, setUnassignedDrawerOpen] = useState(initialSettings.unassignedDrawerOpen);

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => startDate.add(i, 'day')), [startDate]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [currentShift, setCurrentShift] = useState<Shift | null>(null);
  const [search, setSearch] = useState<string | null>('');
  const [filteredGroups, setFilteredGroups] = useState<Group[]>(groups);
  const [accordionStates, setAccordionStates] = useState<Record<string, boolean>>({ internal: true });
  const [providerSorting, setProviderSorting] = useState<'asc' | 'desc' | null>(null);

  useEffect(() => {
    onSortingChange && onSortingChange('name', providerSorting);
  }, [providerSorting]);
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSelectedShift(null);
      }
    };
    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, []);

  useEffect(() => {
    localStorage.setItem(
      LS_CalendarSettings,
      JSON.stringify({ calendarWidth: width, calendarHeight: height, unassignedDrawerOpen: isUnassignedDrawerOpen }),
    );
    if (!isUnassignedDrawerOpen) {
      setSelectedShift(null);
    }
  }, [width, height, isUnassignedDrawerOpen]);

  const leftBoxStyles = useMemo(
    () => ({
      minWidth: `${width}px`,
      marginRight: '16px',
    }),
    [width],
  );

  const columnResizerStyles = useMemo(
    () => ({
      marginLeft: `${width}px`,
    }),
    [width],
  );

  useEffect(() => {
    if (!search) {
      setFilteredGroups(groups);
      return;
    }
    const lowerSearch = search.toLowerCase().trim().split(' ');
    const filtered = groups
      .map((group) => ({
        ...group,
        items: (group?.items || []).filter((row) => {
          const hystack = [
            row.resourceInfo.firstName,
            row.resourceInfo.lastName,
            row.resourceInfo.agency?.name,
            row.resourceInfo.qualifications.map((q) => q.abbreviation).join(' '),
            row.resourceInfo.staffingType,
          ]
            .join(' ')
            .toLowerCase();
          return lowerSearch.some((term) => hystack.includes(term));
        }),
      }))
      .filter((group) => group.items.length > 0);

    setFilteredGroups(filtered);
  }, [search, groups]);

  const handleSearchChange: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> = (evt) => {
    const searchValue = evt.target.value.toLowerCase();
    setSearch(searchValue);
  };

  const handleShiftClick = (shift: Shift) => {
    setAnchorEl(null);
    const isSingleShift = shift && shift.recurrence === 'single';
    const isSingleAssignee = shift && !isSingleShift && !shift.canHasMultipleAssignee;
    const isPartOfSeries = shift && shift.recurrence === 'series';
    const isPartOfRotation = shift && shift.recurrence === 'rotation';
    if (!isSingleShift && isSingleAssignee) {
      !!isPartOfSeries && onShiftOpenSeries && onShiftOpenSeries(shift);
      !!isPartOfRotation && onShiftOpenRotation && onShiftOpenRotation(shift);
      setAnchorEl(null);
      return;
    } else {
      onShiftEdit && onShiftEdit(shift);
    }
  };
  const handleShiftDBClick = (shift: Shift) => {
    handleShiftClick(shift);
  };

  const groupsSanitized = filteredGroups.filter(Boolean);

  return (
    <CalendarWrapper>
      <CalendarPaper>
        {/* Heading */}
        <ColumnResizeController sx={columnResizerStyles} onMouseDown={startWidthResize} />
        {dataLoading && (
          <LinearProgress
            sx={{ position: 'absolute', width: '100%', height: '2px', left: 0, top: '47px', zIndex: 1000 }}
          />
        )}
        <Stack direction={'row'} sx={{ height: '35px' }}>
          <Stack sx={leftBoxStyles}>
            <ProviderSearchInput
              label="Providers"
              variant="standard"
              hiddenLabel={true}
              onChange={handleSearchChange}
              InputProps={{
                endAdornment: (
                  <Stack direction={'row'} spacing={0.5} alignItems="center">
                    <Typography variant="subtitle2" sx={{ opacity: 0.4, userSelect: 'none' }}>
                      {providers?.length || 0}
                    </Typography>
                    <Stack
                      direction={'row'}
                      justifyContent={'center'}
                      alignContent={'center'}
                      sx={{
                        cursor: 'pointer',
                        width: 16,
                        opacity: providerSorting ? 1 : 0.4,
                        ':hover': {
                          backgroundColor: theme.palette.action.hover,
                          opacity: 1,
                        },
                        alignContent: 'center',
                      }}
                      onClick={() => {
                        setProviderSorting((prev) => {
                          if (prev === 'asc') return 'desc';
                          if (prev === 'desc') return null;
                          return 'asc';
                        });
                      }}
                    >
                      <FaIcon name={providerSorting === 'asc' ? 'arrow-down' : 'arrow-up'} />
                    </Stack>
                  </Stack>
                ),
              }}
            />
          </Stack>
          {days.map((day, index) => (
            <Box
              key={index}
              sx={{
                minWidth: '120px',
                width: `100%`,
                display: 'flex',
                flexGrow: 1,
                paddingLeft: 1,
                borderBottomColor: dayjs().isSame(day, 'day') ? alpha(theme.palette.primary.light, 0.3) : 'transparent',
                borderBottomStyle: 'solid',
                borderBottomWidth: dayjs().isSame(day, 'day') ? '4px' : '0px ',
                borderRight: `1px solid ${theme.palette.divider}`,
                userSelect: 'none',
              }}
            >
              <Typography variant="subtitle1" sx={{ marginLeft: '16px' }}>
                {day.format('ddd, MMM D')}
              </Typography>
            </Box>
          ))}
        </Stack>
        {/* Content */}
        <RoundPaper
          sx={{
            padding: 0,
            height: 'calc(100% - 30px)',
            maxHeight: 'calc(100% - 30px)',
            overflowY: 'auto',
            opacity: dataLoading ? 0.5 : 1,
          }}
        >
          {groupsSanitized.length
            ? groupsSanitized.map((group) => (
                <Accordion
                  expanded={accordionStates[group.groupId] || false}
                  key={group.groupId}
                  onChange={() => {
                    setAccordionStates((prev) => ({ ...prev, [group.groupId]: !prev[group.groupId] }));
                  }}
                  disableGutters
                  elevation={0}
                  square
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      backgroundColor: '#F5F5F5',
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{
                        maxWidth: `calc(${width}px - 60px)`,
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {group.groupId === 'internal' ? data?.name : group.groupName}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ padding: 0 }}>
                    {accordionStates[group.groupId] &&
                      group.items.map((row, index) => (
                        <CalendarRow
                          key={index}
                          resourceInfo={row.resourceInfo}
                          assignments={row.assignments}
                          startDate={startDate}
                          resourceColumnWidth={width}
                          onShiftContextMenu={(shift, event) => {
                            event.preventDefault();
                            setCurrentShift(shift);
                            setAnchorEl(event.currentTarget);
                          }}
                          onShiftCardClick={handleShiftClick}
                          onAssignButtonClick={onAssignMenuOpen}
                          onAssign={onAssign}
                          onShiftCardDBClick={handleShiftDBClick}
                          onAvatarClick={onResourceClick}
                        />
                      ))}
                  </AccordionDetails>
                </Accordion>
              ))
            : null}
          {!!groupsSanitized.length && <Box sx={{ height: '50%' }} />}
          {!groupsSanitized.length && (
            <EmptyListPlaceholder
              icon={<ShiftsIcon active />}
              title={<FormattedMessage id="shifts.table.noRows.title" />}
              subtitle={<FormattedMessage id="shifts.table.noRows.subtitle" />}
            />
          )}

          <ShiftContextMenu
            anchorEl={anchorEl}
            currentShift={currentShift}
            onClose={() => {
              setAnchorEl(null);
              setCurrentShift(null);
            }}
            onShiftCancelCall={onShiftCancelCall}
            onShiftDeleteCall={onShiftDeleteCall}
            onShiftUnassignCall={onShiftUnassignCall}
            onShiftHardDeleteCall={onShiftHardDeleteCall}
            onShiftEdit={onShiftEdit}
            onShiftOpenSeries={onShiftOpenSeries}
            onShiftOpenRotation={onShiftOpenRotation}
          />

          {groupsSanitized.length && (
            <UnassignedShiftsDrawer
              width={width}
              height={height}
              onResize={startHeightResize}
              shiftsList={unassigned}
              open={isUnassignedDrawerOpen}
              onDrawerOpen={() => setUnassignedDrawerOpen(!isUnassignedDrawerOpen)}
              onShiftContextMenu={(shift, event) => {
                event.preventDefault();
                setCurrentShift(shift);
                setAnchorEl(event.currentTarget);
              }}
              onShiftEdit={onShiftEdit}
            />
          )}
        </RoundPaper>
        {dataLoading && (
          <Box
            sx={{
              position: 'absolute',
              backgroundColor: 'transparent',
              width: '100%',
              height: '100%',
              top: 0,
              zIndex: 1000,
            }}
          />
        )}
      </CalendarPaper>
    </CalendarWrapper>
  );
}
const CalendarWrapper = styled(Box)(({ theme }) => ({
  height: 'calc(100% - 70px)',
  overflowX: 'auto',
  overflowY: 'hidden',
}));

const CalendarPaper = styled(PrimaryPaper)(({ theme }) => ({
  height: 'calc(100% - 0px)',
  minWidth: '1200px',
  minHeight: 0,
  mt: 2,
  position: 'relative',
}));

const ProviderSearchInput = styled(TextField)(({ theme }) => ({
  width: '100%',
  marginTop: '-13px',
  '&::before': {
    borderBottom: '2px solid red',
  },
  '&:hover:before': {
    borderBottom: '2px solid red',
  },
  '&.Mui-focused:after': {
    borderBottom: '2px solid red',
  },
}));

const ColumnResizeController = styled(Box)(({ theme }) => ({
  position: 'absolute' as const,
  width: '16px',
  zIndex: 100,
  minWidth: '16px',
  height: '100%',
  background: `color-mix(in srgb, #fff 100%, ${theme.palette.primary.main} 8%)`,
  cursor: 'col-resize' as const,
  userSelect: 'none' as const,
}));

type CalendarRowProps = {
  startDate: Dayjs;
  resourceInfo: StaffListItem;
  assignments: Shift[];
  resourceColumnWidth: number;
  onShiftContextMenu: (shift: Shift, event: React.MouseEvent<HTMLElement>) => void;
  onShiftCardClick: (shift: Shift) => void;
  onAssignButtonClick: (provider: StaffListItem, assignments: Shift[], day: Dayjs) => void;
  onAssign: (provider: StaffListItem, shift: Shift) => Promise<void>;
  onShiftCardDBClick: (shift: Shift, event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  onAvatarClick?: (provider: StaffListItem) => void;
};

export const CalendarRow = React.memo(function CalendarRow({
  resourceInfo,
  assignments,
  startDate,
  resourceColumnWidth,
  onShiftContextMenu,
  onShiftCardClick,
  onAssignButtonClick,
  onAssign,
  onShiftCardDBClick,
  onAvatarClick,
}: CalendarRowProps) {
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => startDate.add(i, 'day')), [startDate]);

  return (
    <Stack direction={'row'} sx={{ height: '100%' }}>
      {/* Resource Column */}
      <Box
        sx={{
          minWidth: `${resourceColumnWidth}px`,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          height: 'auto',
          minHeight: '100%',
          marginRight: '16px',
        }}
      >
        <ResourceItem resourceInfo={resourceInfo} assignments={assignments} onAvatarClick={onAvatarClick} />
      </Box>
      {/* Days Columns */}
      {days.map((day, index) => (
        <Box
          key={index}
          sx={{
            minWidth: '120px',
            width: `100%`,
            display: 'flex',
            flexGrow: 1,
          }}
        >
          <CalendarCell
            resourceInfo={resourceInfo}
            assignments={assignments}
            day={day}
            onShiftContextMenu={onShiftContextMenu}
            onShiftCardClick={onShiftCardClick}
            onAssignButtonClick={onAssignButtonClick}
            onAssign={onAssign}
            onShiftCardDBClick={onShiftCardDBClick}
          />
        </Box>
      ))}
    </Stack>
  );
});

type ResourceItemProps = {
  resourceInfo: StaffListItem;
  assignments: Shift[];
  onAvatarClick?: (provider: StaffListItem) => void;
};

export const ResourceItem = React.memo(function ResourceItem({
  resourceInfo,
  assignments,
  onAvatarClick,
}: ResourceItemProps) {
  const intl = useIntl();
  const { current } = useLocationStore();
  const theme = useTheme();
  const weekHours = 40;
  const hoursScheduled = assignments
    .map((shift) =>
      shift?.endDate && shift?.startDate ? dayjs(shift.endDate).diff(dayjs(shift.startDate), 'hours') : 0,
    )
    .reduce((total, hours) => {
      return total + hours;
    }, 0);

  const handleAvatarClick = () => {
    onAvatarClick && onAvatarClick(resourceInfo);
  };

  const showAward = !!resourceInfo.credentials.find((cred) => cred.type === 'License');
  const isOriented = resourceInfo.orientedList.find((o) => o.locationId === current?.id)?.value;

  return (
    <Stack direction={'row'} sx={{ height: '100%', width: '100%' }}>
      <Stack
        direction={'row'}
        sx={{
          width: 'calc(100% - 2px)',
          height: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          padding: 2,
          borderBottom: '1px solid #e0e0e0',
        }}
        spacing={1}
      >
        <Box sx={{ position: 'relative' }}>
          <Tooltip
            title={
              resourceInfo.staffingType === 'Internal'
                ? intl.formatMessage({ id: `${'timecards.listPage'}.table.fields.staffMember.internalTooltip` })
                : intl.formatMessage({ id: `${'timecards.listPage'}.table.fields.staffMember.externalTooltip` })
            }
            arrow
          >
            <AvatarInt
              onClick={handleAvatarClick}
              sx={{
                cursor: onAvatarClick ? 'pointer' : 'default',
                outline: (theme) =>
                  `2px solid ${resourceInfo.staffingType === 'Internal' ? theme.palette.info.main : theme.palette.warning.main}`,
                border: '1px solid white',
              }}
              src={`${resourceInfo.imageUrl ?? ''}`}
              fullName={`${resourceInfo.firstName} ${resourceInfo.lastName}`}
              alt={`${resourceInfo.firstName} ${resourceInfo.lastName}`}
            />
          </Tooltip>
          {showAward && (
            <Tooltip title="Licensed provider">
              <Box
                sx={{
                  position: 'absolute',
                  top: 26,
                  left: 26,
                }}
              >
                <FaIcon
                  name="certificate"
                  color="#ffa733"
                  variant="solid"
                  style={{
                    fontSize: '14px',
                    borderRadius: '50%',
                    width: '16px',
                    height: '16px',
                    padding: '2px',
                  }}
                />
              </Box>
            </Tooltip>
          )}
          {isOriented && (
            <Tooltip title="Location oriented provider">
              <Box
                sx={{
                  position: 'absolute',
                  top: -6,
                  left: 26,
                }}
              >
                <FaIcon
                  name="square-check"
                  color={'#4caf50'}
                  variant="solid"
                  style={{
                    fontSize: '14px',
                    backgroundColor: theme.palette.background.paper,
                    borderRadius: '50%',
                    width: '16px',
                    height: '16px',
                  }}
                />
              </Box>
            </Tooltip>
          )}
        </Box>
        <Stack direction={'column'} spacing={1}>
          <Typography
            variant="subtitle2"
            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', maxHeight: '1.2em', width: '100%' }}
          >{`${resourceInfo.firstName} ${resourceInfo.lastName}`}</Typography>
          <Stack
            direction={'row'}
            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', userSelect: 'none' }}
            spacing={1}
          >
            {resourceInfo.agency?.logoUrl && (
              <Tooltip title={resourceInfo.agency?.name || ''} placement="top">
                <Avatar
                  src={resourceInfo.agency?.logoUrl}
                  sx={{ width: '16px', height: '16px', top: '4px', marginRight: 1 }}
                />
              </Tooltip>
            )}
            {resourceInfo.qualifications.map((qual, index) => (
              <Chip size="small" label={qual.abbreviation} key={index} />
            ))}
          </Stack>
          <Stack
            direction={'row'}
            spacing={1}
            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', userSelect: 'none' }}
          >
            <Stack direction={'row'} sx={{ marginTop: '4px', marginLeft: '4px', marginRight: '4px' }} spacing={0.5}>
              <FaIcon
                name="clock"
                variant="solid"
                width={16}
                height={16}
                color={
                  hoursScheduled > weekHours
                    ? theme.palette.error.main
                    : hoursScheduled === weekHours
                      ? theme.palette.success.main
                      : theme.palette.text.primary
                }
              />
              <Typography
                sx={(theme) => ({
                  fontFamily: 'Roboto',
                  fontSize: '12px',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  color:
                    hoursScheduled > weekHours
                      ? theme.palette.error.main
                      : hoursScheduled === weekHours
                        ? theme.palette.success.main
                        : theme.palette.text.primary,
                })}
              >
                {hoursScheduled} / {weekHours}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
});

const StatusesColorMap: Record<string, 'info' | 'warning' | 'success' | 'error' | 'default' | 'primary' | 'secondary'> =
  {
    Opened: 'info',
    Pending: 'warning',
    Assigned: 'success',
    Unassigned: 'warning',
    Reopened: 'info',
    NoShow: 'error',
    InProgress: 'info',
    Completed: 'success',
    Canceled: 'info',
    Reassigned: 'warning',
  };

type CalendarCellProps = {
  resourceInfo: StaffListItem;
  assignments: Shift[];
  day: Dayjs;
  onShiftContextMenu: (shift: Shift, event: React.MouseEvent<HTMLElement>) => void;
  onShiftCardClick: (shift: Shift) => void;
  onAssignButtonClick: (provider: StaffListItem, assignments: Shift[], day: Dayjs) => void;
  onAssign: (provider: StaffListItem, shift: Shift) => Promise<void>;
  onShiftCardDBClick: (shift: Shift, event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
};

export const CalendarCell = ({
  resourceInfo,
  assignments,
  day,
  onShiftContextMenu,
  onShiftCardClick,
  onAssignButtonClick,
  onAssign,
  onShiftCardDBClick,
}: CalendarCellProps) => {
  const theme = useTheme();
  const [currentShift, setCurrentShift] = useState<Shift | null>(null);
  const { selectedShift } = useCalendarShiftsStore();

  const shifts = useMemo(
    () => assignments.filter((assignment) => assignment && dayjs(assignment.startDate).isSame(day, 'day')),
    [assignments, day],
  );

  let isAssignable =
    selectedShift &&
    day.isSame(dayjs(selectedShift?.startDate), 'day') &&
    resourceInfo.qualifications.some((qualification) =>
      selectedShift.nurseQualifications.some((shiftQualification) => shiftQualification.id === qualification.id),
    ) &&
    !['expired', 'canceled'].includes(selectedShift.status.toLowerCase());

  if (selectedShift?.recurrenceId && resourceInfo.agency) {
    isAssignable = false;
  }

  const handleShiftCardClick = () => {
    if (currentShift) {
      onShiftCardClick(currentShift);
    }
  };

  const handleAddClick = () => {
    onAssignButtonClick && onAssignButtonClick(resourceInfo, assignments, day);
  };

  const handleAssignHolderClick = async () => {
    if (resourceInfo && selectedShift) {
      onAssign && (await onAssign(resourceInfo, selectedShift));
      setCurrentShift(null);
    }
  };

  return (
    <>
      <Stack
        direction={'column'}
        sx={{
          height: '100%',
          width: '100%',
          borderRight: '1px solid #e0e0e0',
          borderBottom: '1px solid #e0e0e0',
          padding: 1,
          backgroundColor: dayjs().isSame(day, 'day') ? alpha(theme.palette.primary.light, 0.12) : 'transparent',
        }}
      >
        {shifts.map((shift, index) => (
          <ShiftItem
            shift={shift}
            key={index}
            onShiftContextMenu={onShiftContextMenu}
            onShiftCardClick={handleShiftCardClick}
            onShiftCardDBClick={onShiftCardDBClick}
          />
        ))}
        <Box flex={1} />
        {selectedShift &&
          (isAssignable ? (
            <Tooltip title="Assign shift here">
              <Box
                onClick={handleAssignHolderClick}
                sx={{
                  height: '64px',
                  width: '100%',
                  border: `1px solid ${theme.palette.info.light}`,
                  borderRadius: theme.spacing(1),
                  background: alpha(theme.palette.info.light, 0.2),
                  cursor: 'pointer',
                }}
              />
            </Tooltip>
          ) : (
            <Box sx={{ height: '64px' }} />
          ))}
        {!selectedShift && (
          <Button onClick={handleAddClick} sx={{ height: '64px' }}>
            <FaIcon name="plus" width="16" height="16" color={theme.palette.text.disabled} />
          </Button>
        )}
      </Stack>
    </>
  );
};

type ShiftItemProps = StackOwnProps & {
  onShiftContextMenu: (shift: Shift, event: React.MouseEvent<HTMLElement>) => void;
  onShiftCardClick: (shift: Shift, event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  onShiftCardDBClick: (shift: Shift, event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  shift: Shift;
  selected?: boolean;
};

export const ShiftItem = ({
  onShiftContextMenu,
  onShiftCardClick,
  onShiftCardDBClick,
  shift,
  selected,
  ...props
}: ShiftItemProps) => {
  const intl = useIntl();
  const theme = useTheme();

  const handleShiftCardDbClick = (evt: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    evt.preventDefault();
    evt.stopPropagation();
    onShiftCardDBClick && onShiftCardDBClick(shift, evt);
  };

  const handleShiftCardClick = (shift: Shift, evt: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    onShiftCardClick && onShiftCardClick(shift, evt);
  };
  const isSeries = shift.recurrence === 'series';
  const isRotation = shift.recurrence === 'rotation';
  const shouldShowaAssigneesType = isRotation || isSeries;
  const isMultiple = shouldShowaAssigneesType && shift.canHasMultipleAssignee === true;
  const isSingle = shouldShowaAssigneesType && shift.canHasMultipleAssignee === false;

  return (
    <Tooltip
      title={shift.assignRequest?.status === 'Pending' ? 'This shift has a pending assignment request' : ''}
      placement="top"
    >
      <ShiftItemWrapper
        onClick={(e) => {
          e.preventDefault();
          handleShiftCardClick(shift, e);
        }}
        onDoubleClick={handleShiftCardDbClick}
        onContextMenu={(e) => {
          onShiftContextMenu(shift, e);
        }}
        {...props}
        selected={shift.assignRequest?.status === 'Pending' || selected}
        borderColor={
          shift.assignRequest?.status === 'Pending'
            ? theme.palette.warning.light
            : theme.palette.shiftType[getShiftType(dayjs(shift.startDate))]
        }
        direction={'column'}
      >
        <Box
          sx={{
            height: '100%',
            padding: 1,
            borderLeft: `6px solid ${theme.palette.shiftType[getShiftType(dayjs(shift.startDate))]}`,
            ...(selected
              ? {
                  background: `linear-gradient(90deg,${alpha(theme.palette.shiftType[getShiftType(dayjs(shift.startDate))], 0.3)} 0%, rgba(0, 0, 0, 0) 71%)`,
                }
              : {}),
          }}
        >
          <Stack direction={'row'} alignItems={'center'} sx={{ userSelect: 'none' }}>
            <Tooltip title={shift.nurseQualifications.map((qual) => qual.abbreviation).join('/')} placement="top">
              <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {shift.nurseQualifications.map((qual) => qual.abbreviation).join('/')}
              </Typography>
            </Tooltip>
            <Box flex={1} />
            <Chip
              label={intl.formatMessage({
                id: `shifts.status.${shift.status.toLowerCase()}`,
              })}
              color={StatusesColorMap[shift.status]}
              size="small"
            />
          </Stack>
          <Stack direction={'row'} sx={{ userSelect: 'none' }}>
            <Typography variant="caption" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {`${dayjs(shift.startDate).format('h:mma')} - ${dayjs(shift.endDate).format('h:mma')}`}
            </Typography>
            <Box flex={1} />
            <Typography variant="caption" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {`${dayjs(shift.endDate).diff(dayjs(shift.startDate), 'hours')}h`}
            </Typography>
          </Stack>
          <Stack direction={'row'} spacing={0.5} alignItems={'center'} sx={{ userSelect: 'none' }}>
            {isRotation && (
              <Tooltip title="Shift is a part of a rotation" placement="top">
                <Box color={(theme) => theme.palette.shiftRecurrence.rotation.main}>
                  <RotationIcon />
                </Box>
              </Tooltip>
            )}
            {isSeries && (
              <Tooltip title="Shift is a part of a series" placement="top">
                <Box color={(theme) => theme.palette.shiftRecurrence.series.main}>
                  <SeriesIcon />
                </Box>
              </Tooltip>
            )}

            {isSingle && (
              <Tooltip title="Recurrence assumes single assignee" placement="top">
                <Box>
                  <FaIcon
                    variant="duotone"
                    name="user"
                    height={'10px'}
                    width={'10px'}
                    color={theme.palette.primary.main}
                  />
                </Box>
              </Tooltip>
            )}
            {isMultiple && (
              <Tooltip title="Recurrence assumes multiple assignees" placement="top">
                <Box>
                  <FaIcon
                    variant="duotone"
                    name="users"
                    height={'15px'}
                    width={'15px'}
                    color={theme.palette.primary.main}
                  />
                </Box>
              </Tooltip>
            )}

            {shift.isUrgent && (
              <Tooltip title="Shift is urgent" placement="top">
                <Box>
                  <HotShiftIcon />
                </Box>
              </Tooltip>
            )}
            {shift.isArchive && (
              <Tooltip title="Shift is archived" placement="top">
                <Box sx={{ width: '13px', height: '13px' }}>
                  <ArchivedShiftIcon />
                </Box>
              </Tooltip>
            )}

            <Box flex={1} />
            {!!shift.tags.length && (
              <TooltipNote
                title={shift.tags.map((tag, idx) => (
                  <Chip key={idx} label={tag.trim()} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                ))}
                placement="top"
                popoverTooltipSx={{ px: 2, maxWidth: '300px' }}
              >
                <Box>
                  <FaIcon name="tags" variant="duotone" width="13px" height="13px" color="primary.main" />
                </Box>
              </TooltipNote>
            )}
            {!!shift.notes.length && (
              <TooltipNote
                title={shift.notes.join(', ')}
                placement="top"
                popoverTooltipSx={{ px: 2, maxWidth: '300px' }}
              >
                <Box>
                  <FaIcon name="memo-pad" variant="duotone" color="secondary.main" width="10px" height="10px" />
                </Box>
              </TooltipNote>
            )}
          </Stack>
        </Box>
      </ShiftItemWrapper>
    </Tooltip>
  );
};

const ShiftItemWrapper = styled(Stack, { shouldForwardProp: (prop: string) => !['borderColor'].includes(prop) })<{
  selected?: boolean;
  borderColor?: string;
}>(({ theme, selected, borderColor }) => ({
  minWidth: '112px',
  minHeight: '75px',
  overflow: 'hidden',
  cursor: 'pointer',
  textOverflow: 'ellipsis',
  backgroundColor: theme.palette.background.paper,
  boxSizing: 'content-box',
  border: selected ? `1px solid ${borderColor}` : '1px solid #e0e0e0',
  borderRadius: theme.spacing(1),
  marginBottom: 1,
}));

type ShiftContextMenuProps = {
  anchorEl: HTMLElement | null;
  currentShift: Shift | null;
  onClose: () => void;
  onShiftCancelCall: (shift: Shift) => void;
  onShiftDeleteCall: (shift: Shift) => void;
  onShiftHardDeleteCall: (shift: Shift) => void;
  onShiftUnassignCall: (shift: Shift) => void;
  onShiftEdit: (shift: Shift) => void;
  onShiftOpenSeries: (shift: Shift) => void;
  onShiftOpenRotation: (shift: Shift) => void;
};

export const ShiftContextMenu = React.memo(function CalendarCell({
  anchorEl,
  currentShift,
  onClose,
  onShiftCancelCall,
  onShiftDeleteCall,
  onShiftHardDeleteCall,
  onShiftUnassignCall,
  onShiftEdit,
  onShiftOpenSeries,
  onShiftOpenRotation,
}: ShiftContextMenuProps) {
  const { isShiftnexAdmin } = useAuthStore();

  const handleOpenSeries = () => {
    if (currentShift) {
      onClose();
      onShiftOpenSeries && onShiftOpenSeries(currentShift);
    }
  };

  const handleOpenRotation = () => {
    if (currentShift) {
      onClose();
      onShiftOpenRotation && onShiftOpenRotation(currentShift);
    }
  };

  const handleEditShift = () => {
    if (currentShift) {
      onClose();
      onShiftEdit && onShiftEdit(currentShift);
    }
  };

  const handleUnassignShift = () => {
    if (currentShift) {
      onClose();
      onShiftUnassignCall && onShiftUnassignCall(currentShift);
    }
  };

  const handleCancelShift = () => {
    if (currentShift) {
      onClose();
      onShiftCancelCall && onShiftCancelCall(currentShift);
    }
  };

  const handleDeleteShift = () => {
    if (currentShift) {
      onClose();
      onShiftDeleteCall && onShiftDeleteCall(currentShift);
    }
  };

  const handleHardDeleteShift = () => {
    if (currentShift) {
      onClose();
      onShiftHardDeleteCall && onShiftHardDeleteCall(currentShift);
    }
  };

  const isSingleShift = currentShift && currentShift.recurrence === 'single';
  const isRecurrencePart = !isSingleShift;
  const isSingleAssignee = currentShift && isRecurrencePart && !currentShift.canHasMultipleAssignee;

  const isPartOfSeries = currentShift && currentShift.recurrence === 'series';
  const isPartOfRotation = currentShift && currentShift.recurrence === 'rotation';

  const isEditable = isSingleShift;
  const isUnassignable = isSingleShift || !isSingleAssignee;
  const isCancelable = true;
  const isDeletable = isSingleShift || !isSingleAssignee;

  const shouldShowOpenRotation = isPartOfRotation;
  const shouldShowOpenSeries = isPartOfSeries;

  return (
    <>
      <Menu
        hideBackdrop
        anchorEl={anchorEl}
        onContextMenu={(e) => {
          e.preventDefault();
          onClose();
        }}
        open={Boolean(anchorEl)}
        onClose={onClose}
        onClick={onClose}
      >
        {shouldShowOpenRotation && <MenuItem onClick={handleOpenRotation}>Open Rotation</MenuItem>}
        {shouldShowOpenSeries && <MenuItem onClick={handleOpenSeries}>Open Series</MenuItem>}
        {isEditable && (
          <MenuItem
            onClick={handleEditShift}
            disabled={!(currentShift && EDIT_ALLOWED_STATUSES.includes(currentShift.status.toLowerCase()))}
          >
            Edit
          </MenuItem>
        )}
        {isUnassignable && (
          <MenuItem
            onClick={handleUnassignShift}
            disabled={!(currentShift && UNASSIGN_ALLOWED_STATUSES.includes(currentShift.status.toLowerCase()))}
          >
            Unassign
          </MenuItem>
        )}
        {isCancelable && (
          <MenuItem
            onClick={handleCancelShift}
            disabled={!(currentShift && CANCEL_ALLOWED_STATUSES.includes(currentShift.status.toLowerCase()))}
          >
            Cancel
          </MenuItem>
        )}
        {isDeletable && (
          <MenuItem
            onClick={handleDeleteShift}
            disabled={
              !(
                currentShift &&
                (currentShift.isArchive
                  ? ['expired', 'unfilled', 'completed', ...DELETE_ALLOWED_STATUSES].includes(
                      currentShift.status.toLowerCase(),
                    )
                  : DELETE_ALLOWED_STATUSES.includes(currentShift.status.toLowerCase()))
              )
            }
          >
            Delete
          </MenuItem>
        )}
        {}
        {isShiftnexAdmin && <MenuItem onClick={handleHardDeleteShift}>Hard Delete</MenuItem>}
      </Menu>
    </>
  );
});

type UnassignedShiftsDrawerProps = {
  width: number;
  height: number;
  shiftsList: Shift[];
  onResize: React.MouseEventHandler<HTMLDivElement>;
  onDrawerOpen: (value: boolean) => void;
  open: boolean;
  onShiftContextMenu: (shift: Shift, event: React.MouseEvent<HTMLElement>) => void;
  onShiftEdit: (shift: Shift, event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
};

export const UnassignedShiftsDrawer = React.memo(function CalendarRow({
  width,
  height,
  shiftsList,
  onResize,
  onDrawerOpen,
  open,
  onShiftContextMenu,
  onShiftEdit,
}: UnassignedShiftsDrawerProps) {
  const { selectedShift, setSelectedShift } = useCalendarShiftsStore();

  const handleBadgeClick = () => {
    onDrawerOpen && onDrawerOpen(open);
  };

  const handleShiftCardClick = (shift: Shift) => {
    if (selectedShift?.id === shift.id || ['expired'].includes(shift.status.toLowerCase())) {
      setSelectedShift(null);
      return;
    }
    setSelectedShift(shift);
  };
  const handleContextMenuClick = (shift: Shift, event: React.MouseEvent<HTMLElement>) => {
    onShiftContextMenu && onShiftContextMenu(shift, event);
  };

  const shiftsByDates = shiftsList.reduce(
    (acc, shift) => {
      const date = dayjs(shift.startDate).format('YYYY-MM-DD');
      if (!acc[date]) {
        acc[date] = [];
      }
      acc[date].push(shift);
      return acc;
    },
    {} as Record<string, Shift[]>,
  );

  return (
    <UnassignedDrawerContainer
      sx={{
        marginLeft: `calc(${width + 16}px + 0px)`,
        width: `calc(100% - ${width + 48}px)`,
        height: `${open ? height : 0}px`,
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: `100%`,
          height: `${open ? height : 0}px`,
        }}
      >
        <UnassignedDrawerBadge onClick={handleBadgeClick} sx={{ cursor: 'pointer' }}>
          <Stack direction="column" sx={{ height: '100%', width: '100%' }}>
            <Box flex={1} />
            <Stack direction="row">
              <Box sx={{ width: '8px' }} />
              <KeyboardArrowUpIcon sx={{ marginTop: '-2px', transform: `rotate(${open ? '180deg' : '0deg'})` }} />
              <Typography variant="subtitle2">{`Unassigned shifts (${shiftsList.length})`}</Typography>

              <Box sx={{ width: '16px' }} />
            </Stack>
            <Box flex={1} />
          </Stack>
        </UnassignedDrawerBadge>
        {open && (
          <>
            <UnassignedDrawerResizeController onMouseDown={onResize} />
            <Stack
              sx={{
                height: '100%',
                width: '100%',
                maxWidth: '100%',
                overflowY: 'auto',
                padding: 2,
              }}
            >
              {Object.keys(shiftsByDates).map((date) => (
                <Box key={date} sx={{ marginBottom: 2 }}>
                  <Typography variant="subtitle1" sx={{ marginBottom: 1 }}>
                    {dayjs(date).format('ddd, MMM D')}
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={0.5}
                    sx={{
                      maxWidth: '100%',
                      overflowX: 'auto',
                      paddingY: 1,
                    }}
                  >
                    {shiftsByDates[date].map((shift, index) => (
                      <ShiftItem
                        key={index}
                        selected={selectedShift?.id === shift.id}
                        sx={{ minWidth: '200px' }}
                        shift={shift}
                        onShiftContextMenu={handleContextMenuClick}
                        onShiftCardClick={handleShiftCardClick}
                        onShiftCardDBClick={onShiftEdit}
                      />
                    ))}
                  </Stack>
                </Box>
              ))}
            </Stack>
          </>
        )}
      </Box>
    </UnassignedDrawerContainer>
  );
});

const UnassignedDrawerBadge = styled(Box)(({ theme }) => ({
  position: 'absolute' as const,
  top: `-32px`,
  height: '32px',
  right: -0,
  zIndex: 100,
  background: `#F5F5F5`,
  userSelect: 'none' as const,
  borderLeft: `1px solid ${theme.palette.divider}`,
  borderTop: `1px solid ${theme.palette.divider}`,
  borderTopLeftRadius: '8px',
}));

const UnassignedDrawerContainer = styled(Box)(({ theme }) => ({
  position: 'absolute' as const,
  bottom: '8px',
  zIndex: 100,
  width: '100%',
  background: `#FaFaFa`,
  userSelect: 'none' as const,
}));

const UnassignedDrawerResizeController = styled(Box)(({ theme }) => ({
  // position: 'absolute' as const,
  width: '100%',
  zIndex: 100,

  height: '8px',
  background: theme.palette.divider,
  cursor: 'row-resize' as const,
  userSelect: 'none' as const,
}));
